import type { Observable } from 'rxjs';

import { VERIFICATION_TYPES } from '@fantaz/shared/config/static';
import type {
  CompletePaymentRequest,
  CompleteSessionDirectCashierResponse,
  DepositSessionRequest,
  ITransactionQueryParams,
  PaymentMethodFailureRequest,
  RequestWithdrawalResponse,
  SavePaymentMethodRequest,
  SavePaymentMethodResponse,
  StatusResponse,
  Transaction,
  TransactionListResponse,
  TsevoDirectCashierResponse,
  ValidateUserRequest,
  WithdrawPayload,
  WithdrawSessionRequest,
} from '@fantaz/shared/data-access/models';

export class PaymentSessionExpiredException implements Error {
  static code = 'SESSION_EXPIRED';

  constructor() {}

  get name() {
    return 'PaymentSessionExpired';
  }
  get message() {
    return 'Payment session expired';
  }
}

export type PaymentException = PaymentSessionExpiredException;

export interface WalletService {
  /**
   * Get a deposit session
   */
  getDepositSession(data: DepositSessionRequest): Observable<any>;

  /**
   * Complete deposit transaction
   * @return {Observable<CompleteSessionDirectCashierResponse>}
   * @throws {PaymentException}
   */
  completeDeposit(
    data: CompletePaymentRequest,
  ): Observable<CompleteSessionDirectCashierResponse>;

  /**
   * Prepare payment method data
   */
  preparePaymentMethod(data: SavePaymentMethodRequest): any;

  /**
   * Save payment method
   */
  savePaymentMethod(
    data: SavePaymentMethodRequest,
    GidxURL?: string,
  ): Observable<SavePaymentMethodResponse>;

  /**
   * Request to withdraw funds
   */
  requestToWithdraw(amount: number): Observable<RequestWithdrawalResponse>;

  /**
   * Get user transactions
   */
  getUserTransactions(
    payload: ITransactionQueryParams,
  ): Observable<TransactionListResponse>;

  /**
   * Cancel a withdrawal
   */
  cancelWithdrawal(payload: WithdrawPayload): Observable<Transaction>;

  /**
   * Validate user with payment method
   */
  validateUserWithPaymentMethod(options: ValidateUserRequest): Observable<{
    status: keyof typeof VERIFICATION_TYPES;
  }>;

  /**
   * Register card validation attempt
   */
  registerCardValidationAttempt(): Observable<StatusResponse>;

  /**
   * Register payment method failure
   */
  registerPaymentMethodFailure(
    payment: PaymentMethodFailureRequest,
  ): Observable<StatusResponse>;

  /**
   * Register payment method warning
   */
  registerPaymentMethodWarning(
    payment: PaymentMethodFailureRequest,
  ): Observable<StatusResponse>;

  /**
   * Get withdraw session
   */
  getWithdrawSession(
    data: WithdrawSessionRequest,
  ): Observable<TsevoDirectCashierResponse>;

  /**
   * Complete withdrawal transaction
   */
  completeWithdrawal(
    data: CompletePaymentRequest,
  ): Observable<CompleteSessionDirectCashierResponse>;
}
